import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserX, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Small admin-only icon button that opens a confirm-modal then calls
 * DELETE /api/admin/users/{id}. Surfaces on every player row (standings,
 * fixtures, social cards, admin users table). Renders nothing for
 * non-admins, self, or other admins.
 *
 * Props:
 *   userId (string)        — target user id
 *   username (string)      — display name in the confirm modal
 *   targetRole (string)    — target user role (skips button when "admin")
 *   variant ("icon"|"link") — compact pill or text link
 *   onRemoved (fn)         — fires after success; parent can prune its list
 *   testId (string)        — data-testid override
 *   size ("xs"|"sm")       — button size
 */
export default function AdminRemovePlayerButton({
  userId,
  username,
  targetRole,
  variant = "icon",
  onRemoved,
  testId,
  size = "sm",
  className = "",
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");

  // Gate: admins only, never against self or another admin.
  const isAdmin = user && user !== false && user.role === "admin";
  if (!isAdmin) return null;
  if (user.id === userId) return null;
  if (targetRole === "admin") return null;

  const doRemove = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (confirm.trim().toLowerCase() !== (username || "").toLowerCase()) {
      toast.error("Type the username exactly to confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      toast.success(
        `Removed ${data.username || username}` +
        (data.slots_reopened ? ` · ${data.slots_reopened} slot(s) reopened` : "") +
        (data.forfeited_matches ? ` · ${data.forfeited_matches} match(es) forfeited` : "")
      );
      setOpen(false);
      setConfirm("");
      if (onRemoved) onRemoved(userId);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const triggerProps = {
    type: "button",
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); },
    "data-testid": testId || `admin-remove-trigger-${userId}`,
    disabled: busy,
    title: `Remove ${username}`,
  };

  const btn = variant === "link" ? (
    <button
      {...triggerProps}
      className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E11D48] hover:text-[#BE123C] transition-colors ${className}`}
    >
      <UserX className="w-3 h-3" /> Remove
    </button>
  ) : (
    <button
      {...triggerProps}
      className={`inline-flex items-center justify-center border border-[#E11D48]/40 text-[#E11D48] hover:bg-[#E11D48] hover:text-white transition-colors ${size === "xs" ? "w-6 h-6" : "w-7 h-7"} ${className}`}
    >
      <UserX className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
    </button>
  );

  return (
    <>
      {btn}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="admin-remove-modal"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E11D48] max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <UserX className="w-4 h-4 text-[#E11D48]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">// admin remove</span>
            </div>
            <h3 className="font-heading text-2xl uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5]">
              Remove <span className="text-[#E11D48]">{username}</span>?
            </h3>
            <p className="font-body text-sm text-[#71717A] mt-3 leading-relaxed">
              This permanently deletes the account. If their league hasn't had any confirmed match yet,
              the slot reopens. Otherwise their remaining matches become <span className="text-[#E11D48]">3-0 forfeits</span> for opponents.
              This cannot be undone.
            </p>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={`Type "${username}" to confirm`}
              autoFocus
              data-testid="admin-remove-confirm-input"
              className="mt-4 w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#E11D48] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            />
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={doRemove}
                disabled={busy || confirm.trim().toLowerCase() !== (username || "").toLowerCase()}
                data-testid="admin-remove-submit"
                className="inline-flex items-center gap-2 bg-[#E11D48] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 disabled:opacity-40 hover:bg-[#BE123C] transition-colors"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />}
                {busy ? "REMOVING…" : "CONFIRM REMOVE"}
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); setConfirm(""); }}
                data-testid="admin-remove-cancel"
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] px-3 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
